import { z } from 'zod';
import { spacesQueryFiltersSchema } from '../spaces/space-filters';

export const ViewTypeEnum = z.enum(['SPACE_SIDEBAR', 'SPACE_LIST']);
export type ViewType = z.infer<typeof ViewTypeEnum>;

export const SpaceSidebarColumns = [
  'tasks',
  'comments',
  'activities',
  'forms',
] as const;
export type SpaceSidebarColumn = (typeof SpaceSidebarColumns)[number];

export const SpaceListColumns = [
  // Exclude 'companyName' (always first) and 'actions' (always last)
  'score',
  'templateName',
  'mapStatus',
  'mapProgress',
  'currentMilestone',
  'lastCompletedMilestone',
  'currentOpenTask',
  'currentOpenTaskScheduled',
  'nextDueDate',
  'lastCompletedTaskDate',
  'lastCompletedTask',
  'createdAt',
  'updatedAt',
  'ownerId',
  'lastVisit',
  'visits',
  'activitiesCount',
  'teams',
] as const;
export type SpaceListColumn = (typeof SpaceListColumns)[number];

export const createColumnSettingsSchema = <T extends string>(
  columns: readonly T[],
) => {
  return z.object({
    order: z.array(z.enum(columns as [T, ...T[]])),
    visible: z.record(z.enum(columns as [T, ...T[]]), z.boolean()),
  });
};

export const SpaceSidebarColumnSettingsSchema =
  createColumnSettingsSchema(SpaceSidebarColumns);
export type SpaceSidebarColumnSettings = z.infer<
  typeof SpaceSidebarColumnSettingsSchema
>;

// For SPACE_LIST, settings includes order, visible, name, emoji, isDefault
export const SpaceListColumnSettingsSchema = createColumnSettingsSchema(
  SpaceListColumns,
).extend({
  name: z.string().optional(),
  emoji: z.string().optional(),
  isDefault: z.boolean().optional(),
  filters: z.object(spacesQueryFiltersSchema.shape).nullable().optional(),
});
export type SpaceListColumnSettings = z.infer<
  typeof SpaceListColumnSettingsSchema
>;

export const ViewSettingsBaseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  tenantId: z.string(),
  viewType: ViewTypeEnum,
  settings: z.union([
    SpaceSidebarColumnSettingsSchema,
    SpaceListColumnSettingsSchema,
  ]),
  createdAt: z.coerce.string(),
  updatedAt: z.coerce.string(),
});

export const ViewSettingsSchema = z.discriminatedUnion('viewType', [
  z.object({
    ...ViewSettingsBaseSchema.shape,
    viewType: z.literal('SPACE_SIDEBAR'),
    settings: SpaceSidebarColumnSettingsSchema,
  }),
  z.object({
    ...ViewSettingsBaseSchema.shape,
    viewType: z.literal('SPACE_LIST'),
    settings: SpaceListColumnSettingsSchema,
  }),
]);

export const SpaceListViewSettingsSchema = z.object({
  ...ViewSettingsBaseSchema.shape,
  viewType: z.literal('SPACE_LIST'),
  settings: SpaceListColumnSettingsSchema,
});

export type TViewSettings = z.infer<typeof ViewSettingsSchema>;

export type TSpaceListViewSettings = Extract<
  TViewSettings,
  { viewType: 'SPACE_LIST' }
>;

export type TSpaceSidebarViewSettings = Extract<
  TViewSettings,
  { viewType: 'SPACE_SIDEBAR' }
>;

export const DEFAULT_SPACE_SIDEBAR_SETTINGS: SpaceSidebarColumnSettings = {
  order: ['tasks', 'comments', 'activities', 'forms'],
  visible: {
    tasks: true,
    comments: true,
    activities: true,
    forms: true,
  },
};

export const DEFAULT_SPACE_LIST_SETTINGS: SpaceListColumnSettings = {
  order: [
    'score', // Engagement last 14 days
    'createdAt', // Created at
    'ownerId', // Owner
    'lastVisit', // Last visit
    'visits', // Visits
    'activitiesCount', // Activities count
    'teams', // Teams
    'mapStatus', // Status Action plan
    'mapProgress', // Progress Action plan
    'currentMilestone', // Current milestone Action plan
    'nextDueDate', // Next due date Action plan
    'lastCompletedMilestone', // Last completed milestone Action plan
    'currentOpenTask', // Current open task Action plan
    'currentOpenTaskScheduled', // Current scheduled open task Action plan
    'lastCompletedTaskDate', // Last completed task date Action plan
    'lastCompletedTask', // Last completed task Action plan
    'templateName', // Template (placed last)
    'updatedAt', // Updated at (placed last)
  ],
  visible: {
    score: true,
    templateName: false,
    mapStatus: true,
    mapProgress: true,
    currentMilestone: false,
    lastCompletedMilestone: false,
    currentOpenTask: false,
    currentOpenTaskScheduled: false,
    nextDueDate: false,
    lastCompletedTaskDate: false,
    lastCompletedTask: false,
    createdAt: true,
    updatedAt: false,
    ownerId: true,
    lastVisit: true,
    visits: true,
    activitiesCount: true,
    teams: true,
  },
  name: 'My Spaces',
  emoji: '',
  isDefault: false,
  filters: {
    spaceType: 'active',
  },
};
